import { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';

const InputStyle = "block w-full mb-3 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none transition text-sm";
const LabelStyle = "block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 mt-2";
const SaveButtonStyle = "w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition shadow-sm mt-2";
const ToggleButtonStyle = "text-sm font-medium py-2 px-4 rounded-lg transition border border-gray-200 hover:bg-gray-50 text-gray-600";

const AddKidForm = () => {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [clothesSize, setClothesSize] = useState('');
  const [shoeSize, setShoeSize] = useState('');
  const { user } = useAuth();

  const handleAddKid = async () => {
    if (!name || !birthdate || !user) return;
    try {
      await addDoc(collection(db, 'kids'), {
        name,
        birthDate: birthdate,
        clothesSize,
        shoeSize,
        userId: user.uid,
        createdAt: new Date()
      });
      setName(''); setBirthdate(''); setClothesSize(''); setShoeSize('');
      setShowForm(false);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="mb-4">
      <button onClick={() => setShowForm(!showForm)} className={ToggleButtonStyle}>
        {showForm ? '✕ Cancel' : '+ Add a Kid'}
      </button>
      {showForm && (
        <div className="mt-4 p-5 bg-white rounded-xl border border-gray-200 shadow-lg max-w-sm">
          <label className={LabelStyle}>Basic Info</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Kid's Name" className={InputStyle} />
          <input
            type="date"
            className="p-2 border rounded-lg"
            value={birthdate}
            onChange={(e) => setBirthdate(e.target.value)}
          />
          <label className={LabelStyle}>Sizes</label>
          <div className="grid grid-cols-2 gap-2">
            <input value={clothesSize} onChange={e => setClothesSize(e.target.value)} placeholder="Clothes" className={InputStyle} />
            <input value={shoeSize} onChange={e => setShoeSize(e.target.value)} placeholder="Shoe" className={InputStyle} />
          </div>
          <button onClick={handleAddKid} className={SaveButtonStyle}>Save Profile</button>
        </div>
      )}
    </div>
  );
};

const AddRoutineForm = ({ kidId, category }) => {
  const [showForm, setShowForm] = useState(false);
  const [activity, setActivity] = useState('');
  const [time, setTime] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);

  const days = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  
  // Theme coloring based on category
  const isActivity = category === 'activity';
  const themeColor = isActivity ? 'blue' : 'green';

  const toggleDay = (day) => {
    setSelectedDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleSave = async () => {
    if (!activity || !time || selectedDays.length === 0) return;
    try {
      // Both "Activities" and "Schedules" now live in the 'routines' subcollection
      await addDoc(collection(db, 'kids', kidId, 'routines'), {
        activity,
        time,
        days: selectedDays,
        category, // 'activity' or 'schedule'
        createdAt: new Date()
      });
      setActivity(''); setTime(''); setSelectedDays([]); setShowForm(false);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="mt-2">
      <button 
        onClick={() => setShowForm(!showForm)} 
        className={`text-xs font-bold hover:underline text-${themeColor}-600`}
      >
        {showForm ? '✕ Cancel' : `+ Add ${isActivity ? 'Activity' : 'Routine'}`}
      </button>

      {showForm && (
        <div className={`mt-2 p-3 bg-${themeColor}-50 rounded-lg border border-${themeColor}-100`}>
          <input 
            value={activity} 
            onChange={e => setActivity(e.target.value)} 
            placeholder={isActivity ? "Soccer, Piano..." : "Nap, School..."} 
            className={InputStyle} 
          />
          <input value={time} type="time" onChange={e => setTime(e.target.value)} className={InputStyle} />
          
          <div className="flex justify-between mb-3">
            {days.map(day => (
              <button
                key={day}
                onClick={() => toggleDay(day)}
                className={`w-7 h-7 rounded-full text-[10px] font-bold transition-all ${
                  selectedDays.includes(day) 
                    ? `bg-${themeColor}-600 text-white shadow-md` 
                    : 'bg-white text-gray-400 border border-gray-200'
                }`}
              >
                {day}
              </button>
            ))}
          </div>

          <button 
            onClick={handleSave} 
            className={`w-full bg-${themeColor}-600 text-white text-xs py-2 rounded-md font-bold`}
          >
            Save {isActivity ? 'Activity' : 'Routine'}
          </button>
        </div>
      )}
    </div>
  );
};

const AddAppointmentForm = ({ kidId, kidName }) => {
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const { user } = useAuth();

  const handleAddAppointment = async () => {
    if (!type || !date) return;
    await addDoc(collection(db, 'calendar'), { 
      title: type, 
      date, 
      time: time,
      kidId: kidId, 
      kidName: kidName,
      userId: user.uid,
      type: 'kid' 
    });
    setType(''); setDate(''); setTime(''); setShowForm(false);
  };

  return (
    <div className="mt-2">
      <button onClick={() => setShowForm(!showForm)} className="text-xs text-purple-600 font-bold hover:underline">
        {showForm ? '✕ Cancel' : '+ Add Appointment'}
      </button>
      {showForm && (
        <div className="mt-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
          <input value={type} onChange={e => setType(e.target.value)} placeholder="Dentist, Checkup..." className={InputStyle} />
          <div className="grid grid-cols-2 gap-2">
            <input value={date} type="date" onChange={e => setDate(e.target.value)} className={InputStyle} />
            <input value={time} type="time" onChange={e => setTime(e.target.value)} className={InputStyle} />
          </div>
          <button onClick={handleAddAppointment} className="w-full bg-purple-600 text-white text-xs py-2 rounded-md font-bold">Add Appointment</button>
        </div>
      )}
    </div>
  );
};

export { AddKidForm, AddRoutineForm, AddAppointmentForm };